import React, { useCallback, useContext, useMemo, useState } from 'react';
import { GravplatsQuery_node_TEDGAGrav as IGravplatsNode } from '../Queries/_QueryTypes/GravplatsQuery';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import { getAttributeInfo } from '../../../../../../Model/avenyModelUtils';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import AttributeDisplayText from '../../../../../../Components/Attributes/AttributeDisplayText/AttributeDisplayText';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { EDControlledExpansionPanel } from '../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import EDCaptionedValue from '../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import GravTitelKort from '../GravTitelKort';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import {
  MASKED_VALUE_TEXT,
  getMaskedFromCurrentValues,
} from '../../../../_Common/Hooks/useAttributeValues';

const GravModel = clientAvenyModel.GA.BusinessModels.GAGrav;

interface IGravsattaProps {
  sidNr: number;
  nodeData: IGravplatsNode | null | undefined;
  fullPage: boolean;
}

export const Gravsatta: React.FC<IGravsattaProps> = ({
  sidNr,
  nodeData,
  fullPage,
}) => {
  const { avenyCollectionMap } = useContext(ModelContext);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [isChecked, setIsChecked] = useState(false);

  // Underlätta åtkomst av data fr. tidigare upl.omg
  const previousBurialPeriods = useMemo(() => {
    if (!nodeData?._Associations?.UplOmg_Connection?.edges) return [];

    return nodeData._Associations.UplOmg_Connection.edges.map(
      edge => edge.node
    );
  }, [nodeData]);

  // kombinera gravsatta från nuvarande och tidigare upl.omg
  const allGravsatta = useMemo(() => {
    const current = nodeData?._Associations?.Gravsatta_All || [];

    if (!isChecked) {
      return current;
    }

    const previous = previousBurialPeriods.flatMap(
      period => period._Associations?.Gravsatta_All || []
    );

    return [...current, ...previous];
  }, [nodeData, isChecked, previousBurialPeriods]);

  // Håll koll på gravsatta från tidigare upl.omg
  const historicalBurialIds = useMemo(() => {
    if (!isChecked) return new Set<string>();

    const ids = new Set<string>();
    previousBurialPeriods.forEach(period => {
      period._Associations?.Gravsatta_All?.forEach(item => {
        if (item?.id) ids.add(item.id);
      });
    });

    return ids;
  }, [isChecked, previousBurialPeriods]);

  const onChange = useCallback((itemId: string) => {
    setExpanded(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const attrInfoNamnFE = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Gravsatta.Avlidna.Person.NamnFE',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoPersNr = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Gravsatta.Avlidna.Person.PERS_NR',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoFoddDatum = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Gravsatta.Avlidna.Person.Fodelsedatum',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoAvlidenDatum = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Gravsatta.Avlidna.AVLIDEN_DATUM',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoGravsDatum = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Gravsatta.Avlidna.GravsDatum',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  const attrInfoGravTyp = useMemo(
    () =>
      getAttributeInfo(
        GravModel,
        'Gravsatta.Avlidna.GRAVTYP',
        avenyCollectionMap
      ),
    [avenyCollectionMap]
  );

  return (
    <ObjektVySida
      sidNr={sidNr}
      caption={'Gravsatta'}
      collectionName={GravModel.CollectionName}
    >
      <ObjektVyKolumner>
        <ObjektVyKolumn fullPage={fullPage}>
          <GravTitelKort nodeData={nodeData} />
          <ObjektVyKort caption="Gravsatta">
            {(nodeData?.TidigareUplOmg ?? 0) > 0 && (
              <ObjektVyKort noBoxShadow>
                <EDCheckbox
                  label="Visa alla upplåtelseomgångar"
                  checked={isChecked}
                  onCheck={checked => setIsChecked(checked)}
                />
              </ObjektVyKort>
            )}
            {allGravsatta.length > 0 ? (
              allGravsatta.map((item, index) => {
                const itemKey = item?.id ?? `item-${index}`;
                const isExpanded = expanded.has(itemKey);

                return (
                  <ObjektVyKort
                    key={itemKey}
                    className="position-relative"
                    backgroundColor={'white'}
                    cardInCard
                    noPadding
                  >
                    <EDControlledExpansionPanel
                      expanded={isExpanded}
                      onChange={() => onChange(itemKey)}
                      overflowHidden
                      label={
                        <EDTypography
                          variant="h6"
                          color={
                            historicalBurialIds.has(item?.id)
                              ? 'textSecondary'
                              : 'black'
                          }
                          width={'100%'}
                          p={2}
                          noWrap
                        >
                          {getMaskedFromCurrentValues(
                            item,
                            'Avlidna.Person.NamnFE',
                            attrInfoNamnFE.attribute
                          )
                            ? MASKED_VALUE_TEXT
                            : item?._Associations?.Avlidna?._Associations
                                ?.Person?.NamnFE}
                        </EDTypography>
                      }
                    >
                      <EDCaptionedValue
                        label="Personnummer"
                        name={`${attrInfoPersNr.attribute.AttributeName}_${index}`}
                        size="large"
                      >
                        <AttributeDisplayText
                          value={
                            item?._Associations?.Avlidna?._Associations?.Person
                              ?.PERS_NR
                          }
                          attributeModel={attrInfoPersNr.attribute}
                          collection={GravModel}
                          forceText
                          masked={getMaskedFromCurrentValues(
                            item,
                            'Avlidna.Person.PERS_NR',
                            attrInfoPersNr.attribute
                          )}
                        />
                      </EDCaptionedValue>
                      <EDCaptionedValue
                        label="Född"
                        name={`${attrInfoFoddDatum.attribute.AttributeName}_${index}`}
                        size="large"
                      >
                        <AttributeDisplayText
                          value={
                            item?._Associations?.Avlidna?._Associations?.Person
                              ?.Fodelsedatum
                          }
                          attributeModel={attrInfoFoddDatum.attribute}
                          collection={GravModel}
                          forceText
                          masked={getMaskedFromCurrentValues(
                            item,
                            'Avlidna.Person.Fodelsedatum',
                            attrInfoFoddDatum.attribute
                          )}
                        />
                      </EDCaptionedValue>

                      <EDCaptionedValue
                        label="Död"
                        name={`${attrInfoAvlidenDatum.attribute.AttributeName}_${index}`}
                        size="large"
                      >
                        <AttributeDisplayText
                          value={item?._Associations?.Avlidna?.AVLIDEN_DATUM}
                          attributeModel={attrInfoAvlidenDatum.attribute}
                          collection={GravModel}
                          forceText
                          masked={getMaskedFromCurrentValues(
                            item,
                            'Avlidna.AVLIDEN_DATUM',
                            attrInfoAvlidenDatum.attribute
                          )}
                        />
                      </EDCaptionedValue>

                      <EDCaptionedValue
                        label="Gravsatt"
                        name={`${attrInfoGravsDatum.attribute.AttributeName}_${index}`}
                        size="large"
                      >
                        <AttributeDisplayText
                          value={item?._Associations?.Avlidna?.GravsDatum}
                          attributeModel={attrInfoGravsDatum.attribute}
                          collection={GravModel}
                          forceText
                          masked={getMaskedFromCurrentValues(
                            item,
                            'Avlidna.GravsDatum',
                            attrInfoGravsDatum.attribute
                          )}
                        />
                      </EDCaptionedValue>

                      <EDCaptionedValue
                        label="Gravsättningstyp"
                        name={`${attrInfoGravTyp.attribute.AttributeName}_${index}`}
                        size="large"
                      >
                        <AttributeDisplayText
                          value={item?._Associations?.Avlidna?.GRAVTYP}
                          attributeModel={attrInfoGravTyp.attribute}
                          collection={GravModel}
                          forceText
                          masked={getMaskedFromCurrentValues(
                            item,
                            'Avlidna.GRAVTYP',
                            attrInfoGravTyp.attribute
                          )}
                        />
                      </EDCaptionedValue>

                      <EDCaptionedValue
                        label="Djup"
                        name={`djup_${index}`}
                        size="large"
                      >
                        <EDTypography>{item?.DJUP ?? '-'}</EDTypography>
                      </EDCaptionedValue>

                      <EDCaptionedValue
                        label="Placering"
                        name={`placering_${index}`}
                        size="large"
                      >
                        <EDTypography>
                          {item?.Placering2 || item?.Placering || '-'}
                        </EDTypography>
                      </EDCaptionedValue>
                    </EDControlledExpansionPanel>
                  </ObjektVyKort>
                );
              })
            ) : (
              <ObjektVyKort
                className="position-relative"
                backgroundColor={'white'}
                caption="Gravsatta"
              >
                <EDTypography>Inga gravsatta registrerade</EDTypography>
              </ObjektVyKort>
            )}
          </ObjektVyKort>
        </ObjektVyKolumn>
      </ObjektVyKolumner>
    </ObjektVySida>
  );
};

export default Gravsatta;
